import React from "react";

import Correctness from "../panels/correctness";
import AboutDescription from "../panels/about_description";
import DetailedReason from "../panels/detailed_reason";
import ApplicableClauses from "../panels/applicable_clauses";
import RelatedClauses from "../panels/related_clauses";
import IssueReasons from "../panels/issue_reasons";
import Guidance from "../panels/guidance";
import Fallback from "../panels/fallback";
import Accuracy from "../panels/accuracy";
import StandardLanguage from "../panels/standard_language";
import LlmResponse from "../panels/llm_response";
import QueryDomain from "../panels/query_domain";
import Amendments from "../panels/amendments";
import logAmendmentFailures from "modules/documents/actions/log_amendment_failures";
import calculateClauses from "../../../utils/issues/calculate_llm_clauses";

import getIssueTemplatedText from "common/utils/issues/get_issue_templated_text";
import IssueStatusMenu from "../helper_components/issue_status_menu";
import {getFieldTemplateValue} from "utils/get_field_template_value";

function collapseToArray(applicableClausesObj) {
  if (!applicableClausesObj) {
    return [];
  }
  let result = [];
  Object.keys(applicableClausesObj).forEach(applicableClausesGroup => {
    result = [
      ...result,
      ...(applicableClausesObj[applicableClausesGroup] || []),
    ];
  });
  return result;
}

export default function render({
  appType,
  issue,
  zoom,
  containerStyles,
  openTabs,
  onItemOpen,
  selectedReportId,
  project,
  isFlipChecklistIcons,
  organisationId,
  scrollToClause,
  ...props
}) {
  const {hideDocumentRelatedItems} = props;

  const {
    applicableClauses,
    relatedClauses,
    relatedClausesCount,
  } = calculateClauses(
    issue,
    props.documentClauseparts,
    props.applicableClauses,
    props.relatedClauses,
    props.relatedClausesCount,
  );

  const fieldTemplateParams = {
    project,
    issue,
    issueset: props.currentIssuesetItem,
    documentClauses: props.documentClauses,
    topicsById: props.topicsById,
    positiveReasonData: props.positiveReasonData,
    documentClauseparts: props.documentClauseparts,
    selectedReportId,
    useManualCorrections: true,
  };

  return [
    <Correctness
      key="correctness"
      issue={issue}
      project={project}
      zoom={zoom}
      organisationId={organisationId}
      {...props}
    />,
    <DetailedReason
      key="detailed-reason"
      issue={issue}
      project={project}
      zoom={zoom}
      organisationId={organisationId}
      document={props.document}
      documentClauses={props.documentClauses}
      documentClauseparts={props.documentClauseparts}
      topicsById={props.topicsById}
      positiveReasonData={props.positiveReasonData}
      currentIssuesetItem={props.currentIssuesetItem}
      selectedReport={selectedReportId}
      isFlipChecklistIcons={isFlipChecklistIcons}
      preventMemo={props.preventMemo}
      scrollToClause={scrollToClause}
      documentSections={props.documentSections}
    />,
    project.enable_document_amendments && (
      <Amendments
        key="amendments"
        documentHeadings={props.baseDocumentHeadings}
        onAmendmentsOpen={onItemOpen("amendmentsOpen")}
        amendmentsOpen={openTabs.amendmentsOpen}
        issue={issue}
        applyClauseChanges={changes => props.applyClauseChanges(changes)}
        completedClauseChanges={props.completedClauseChanges}
        clauseChangeErrors={props.clauseChangeErrors}
        setClauseChangeError={props.setClauseChangeError}
        documentClauseparts={props.documentClauseparts}
        chatAmendments={props.chatAmendments}
        bulkApplyingIds={props.bulkApplyingIds}
        removeAppliedAmendments={props.removeAppliedAmendments}
        // The webapp resolves scroll targets via the clause id in the DOM, but
        // amendment objects don't carry that id, so clause links can't resolve
        // there. Word scrolls by anchor text, which amendments do have — so the
        // links are only wired up in Word; elsewhere they render as plain text.
        scrollToClause={appType === "wordTaskpane" ? scrollToClause : undefined}
        onPopOut={props.onPopOutAmendments}
        onLogAmendmentErrors={errors =>
          logAmendmentFailures({
            organisationId,
            projectId: project.id,
            documentId: props.document.id,
            errors,
            documentIssueId: issue.document_issue_id,
          })
        }
      />
    ),
    <IssueStatusMenu
      key="review-tracking"
      issueStatus={issue.review_state}
      issue={issue}
      updateIssueReviewState={props.updateIssueReviewState}
      reviewTrackingOn={props.reviewTrackingOn}
    />,
    ...(props.organisation.use_llm && project.show_llm_response
      ? [
          <LlmResponse
            key="llm-response"
            fetchConversation={props.fetchConversation}
            generateDocumentIssueLlmPrompt={
              props.generateDocumentIssueLlmPrompt
            }
            fetchDocumentIssueConversation={
              props.fetchDocumentIssueConversation
            }
            clearDocumentIssueConversation={
              props.clearDocumentIssueConversation
            }
            issue={issue}
            issueset={props.currentIssuesetItem}
          />,
        ]
      : []),
    <AboutDescription
      key="about-description"
      containerStyles={containerStyles}
      issue={issue}
      show_how_issue_works={
        project.show_how_issue_works || props.force_show_how_issue_works
      }
      aboutDescription={
        getIssueTemplatedText(
          issue,
          props.currentIssuesetItem,
          "about_description",
          props.documentClauses,
          props.topicsById,
          props.positiveReasonData,
          props.documentClauseparts,
          props.document.parties,
          true,
          false,
        )[0]
      }
      onOpen={onItemOpen("aboutDescriptionOpen")}
      isOpen={openTabs.aboutDescriptionOpen}
    />,
    <Guidance
      key="guidance"
      containerStyles={containerStyles}
      guidance={getFieldTemplateValue("guidance", fieldTemplateParams)}
      onOpen={onItemOpen("guidanceOpen")}
      isOpen={openTabs.guidanceOpen}
    />,
    !(issue.hide_applicable_clauses || project.hide_applicable_clauses) && (
      <ApplicableClauses
        key="applicable-clauses"
        applicableClausesReferenced={props.applicableClausesReferenced}
        applicableClausesOpen={openTabs.applicableClausesOpen}
        onApplicableClausesOpen={onItemOpen("applicableClausesOpen")}
        onClauseClick={props.scrollToClauseWithFileIndex}
        onHeadingClick={props.scrollToHeading}
        topicsById={props.topicsById}
        documentHeadings={props.baseDocumentHeadings}
        documentSections={props.documentSections}
        documentDefinitions={props.documentDefinitions}
        documentChanges={props.documentChanges}
        document={props.document}
        parties={props.document.parties}
        roles={props.roles}
        issue={issue}
        selectedReport={selectedReportId}
        correctDocumentIssueManually={props.correctDocumentIssueManually}
        renderStatus={props.renderStatus}
        scrollToParties={props.scrollToParties}
        {...props}
        applicableClauses={applicableClauses}
      />
    ),
    <RelatedClauses
      key="related-clauses"
      relatedClauses={relatedClauses}
      issue={issue}
      onRelatedClausesOpen={onItemOpen("relatedClausesOpen")}
      onClauseClick={props.scrollToClause}
      topicsById={props.topicsById}
      relatedClausesOpen={openTabs.relatedClausesOpen}
      relatedClausesCount={relatedClausesCount}
      documentHeadings={props.baseDocumentHeadings}
      documentSections={props.documentSections}
      documentDefinitions={props.documentDefinitions}
      documentChanges={props.documentChanges}
      document={props.document}
      fileNames={props.fileNames}
      fileIndex={props.fileIndex}
      renderStatus={props.renderStatus}
    />,
    !project.enable_document_amendments && (
      <StandardLanguage
        key="standard-language"
        issue={issue}
        containerStyles={containerStyles}
        standardLanguageText={getFieldTemplateValue(
          "standard_language",
          fieldTemplateParams,
        )}
        onOpen={onItemOpen("standardLanguageOpen")}
        isOpen={openTabs.standardLanguageOpen}
        templateDocumentLink={
          hideDocumentRelatedItems ? null : project.template_document_link
        }
      />
    ),
    !project.enable_document_amendments && (
      <Fallback
        key="fallback"
        containerStyles={containerStyles}
        fallback={getFieldTemplateValue(
          "fallback_guidance",
          fieldTemplateParams,
        )}
        onOpen={onItemOpen("fallbackOpen")}
        isOpen={openTabs.fallbackOpen}
        organisationId={organisationId}
      />
    ),
    appType !== "wordTaskpane" && (
      <IssueReasons
        key="issue-reasons"
        user={props.user}
        topicsById={props.topicsById}
        containerStyles={containerStyles}
        onOpen={onItemOpen("issueReasonsOpen")}
        isOpen={openTabs.issueReasonsOpen}
        applicableClauses={collapseToArray(props.applicableClauses)}
        onClauseClick={props.scrollToClause}
        issue={issue}
        clientModeOn={props.clientModeOn}
        isFlipChecklistIcons={isFlipChecklistIcons}
      />
    ),
    props.verifyIssues && (
      <Accuracy
        key="accuracy"
        zoom={zoom}
        issue={issue}
        updateDocumentIssue={(issue, updates) =>
          props.updateDocumentIssue(issue, updates, {
            update_override_document_issue_values: true,
            merge_update_object: true,
          })
        }
        isFlipChecklistIcons={isFlipChecklistIcons}
        user={props.user}
      />
    ),
    issue.trigger_type === "LLM" && !props.clientModeOn && (
      <QueryDomain
        value={issue.query_domain}
        onChange={queryDomain =>
          props.updateDocumentIssue(issue, {query_domain: queryDomain})
        }
        issueId={issue.id}
      />
    ),
  ].filter(item => item);
}
